/**
 * The engine: what keeps the data right, behind one API. Interfaces use this
 * module, nothing below it (design note:
 * docs/plans/2026-10-19-engine-interface-separation.md; reference:
 * docs/reference/engine-api.md).
 *
 * Step 1 of the plan: the API over today's modules, which hold one account
 * and one open map per page; so there is one engine per page. Its parts:
 *
 *   engine.map       the open map: its tree, changes, items, tags, notes,
 *                    the journal, routines, the Today list, board columns,
 *                    linked maps, undo, its sync state
 *   engine.maps      the maps of the account (the vault) and opening one
 *   engine.account   who is signed in
 *   engine.on(...)   events: "map:opened", "map:changed", "vault:changed",
 *                    "account:changed", "sync:changed", "notice"
 *
 * Plain data in and out. The tree is read-only: change it through the API.
 * An item the last change did not touch is the same object as before
 * (engine/core/tree-reconcile.js), so an interface can redraw only what changed.
 */
import * as Bridge from "./sync/crdt-bridge.js";
import * as MapState from "./map-state.js";
import { openMap, closeMap, openMapStorage } from "./open-map.js";
import { setLastOpenedDoc } from "../shared/local-doc-state.js";
import { setHost, newItemColor } from "./host.js";
import * as Undo from "./core/undo-manager.js";
import { VaultStorage } from "./crypto/vault-storage.js";
import { getCurrentUser } from "./account.js";
import { on as onEvent, Events } from "./core/events.js";
import { NOTICE_EVENTS } from "./notices.js";
import { exportAllData, importAllData, readExportFile } from "./crypto/data-export.js";
import { deleteWaitingFiles } from "./services/waiting-attachments.js";
import { removeAllMembers } from "./sync/share-rotation.js";
import { storageOf } from "./core/vault-folders.js";
import {
  uploadAttachment, populateAttachmentCache, getAttachmentDek, resolveAttachmentForDisplay,
  attachmentRef, parseAttachmentRef,
} from "./services/attachment-service.js";
import { logout } from "./auth/handlers.js";
import * as History from "./history.js";
import * as LinkSharing from "./sync/link-sharing.js";
import { t } from "../shared/i18n/index.js";

export { NOTICE, QUESTION } from "./notices.js";

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * The engine's events, from today's event bus (engine/core/events.js). Each entry:
 * the bus events it comes from, and how their payload becomes the engine's.
 */
const EVENT_SOURCES = {
  "map:opened": [[Events.MAP_OPENED], (p) => ({ mapId: p?.docId ?? Bridge.getActiveDocId() ?? null })],
  "map:changed": [[Events.CRDT_PROJECTION_UPDATED], (p) => ({
    mapId: Bridge.getActiveDocId(), tree: p?.data ?? null, changes: p?.changes ?? null, origin: p?.origin ?? null,
  })],
  "journal:changed": [[Events.DOC_NOTES_CHANGED], (p) => ({ origin: p?.origin ?? null })],
  "routines:changed": [[Events.ROUTINES_CHANGED], (p) => ({ origin: p?.origin ?? null })],
  "today:changed": [[Events.EXECUTION_CHANGED], (p) => ({ origin: p?.origin ?? null })],
  "vault:changed": [[Events.VAULT_INDEX_UPDATED], () => ({})],
  "account:changed": [[Events.AUTH_LOGIN, Events.AUTH_LOGOUT], () => ({ user: getCurrentUser() })],
  "sync:changed": [[Events.SERVER_STATUS_CHANGED, Events.SERVER_BUSY_CHANGED, Events.SYNC_CONNECTED,
    Events.SYNC_DISCONNECTED, Events.SYNC_PAUSE_CHANGED], () => syncState()],
};

/**
 * Listen to an engine event. Returns the function that stops listening.
 * "notice": something the person should know ({ code, level, data }; the
 * codes in engine/notices.js).
 * @param {string} name
 * @param {(payload: Object) => void} listener
 * @returns {() => void}
 */
function on(name, listener) {
  if (name === "notice") {
    const stops = Object.entries(NOTICE_EVENTS).map(([event, toNotice]) =>
      onEvent(event, (payload) => { const notice = toNotice(payload); if (notice) listener(notice); }));
    return () => stops.forEach((stop) => stop());
  }
  const source = EVENT_SOURCES[name];
  if (!source) throw new Error(`Unknown engine event: ${name}`);
  const [events, convert] = source;
  const stops = events.map((event) => onEvent(event, (payload) => listener(convert(payload))));
  return () => stops.forEach((stop) => stop());
}

// ---------------------------------------------------------------------------
// The open map
// ---------------------------------------------------------------------------

function requireMap() {
  const id = Bridge.getActiveDocId();
  if (!id || !MapState.get("data")) throw new Error("No map is open");
  return id;
}

/** The open map's sync state. */
function syncState() {
  const id = Bridge.getActiveDocId();
  return {
    mapId: id || null,
    connection: Bridge.getConnectionState?.() ?? null,
    busy: !!Bridge.isServerBusy?.(),
    synced: id ? !!Bridge.isDocFullySynced?.(id) : false,
    pendingWrites: !!Bridge.hasPendingWrites?.(),
  };
}

// ---------------------------------------------------------------------------
// Fields an interface adds (design note §3.6, decision 2)
// ---------------------------------------------------------------------------

/**
 * An interface's own fields on items: named "<interface>:<field>"
 * ("holon:effort"), synced and kept like any other field; other interfaces
 * keep them without showing them. Small: a value's JSON at most
 * EXTRA_VALUE_MAX characters, at most EXTRA_FIELDS_MAX fields of one
 * interface on an item. null removes one.
 */
const EXTRA_FIELD = /^([a-z][a-z0-9-]{0,30}):([A-Za-z][A-Za-z0-9_-]{0,40})$/;
export const EXTRA_VALUE_MAX = 1000;
export const EXTRA_FIELDS_MAX = 24;

/** Throws when `fields` holds an interface field that breaks the rules. */
function checkExtraFields(fields, item = null) {
  const added = new Map();
  for (const [key, value] of Object.entries(fields)) {
    if (!key.includes(":")) continue;
    const match = EXTRA_FIELD.exec(key);
    if (!match) throw new Error(`"${key}": an interface's field is named "<interface>:<field>" (letters, digits, - and _)`);
    if (value === null || value === undefined) continue;
    const json = JSON.stringify(value);
    if (json === undefined) throw new Error(`"${key}": its value must be plain data`);
    if (json.length > EXTRA_VALUE_MAX) throw new Error(`"${key}": its value is over ${EXTRA_VALUE_MAX} characters`);
    added.set(match[1], (added.get(match[1]) || new Set()).add(key));
  }
  for (const [prefix, keys] of added) {
    const kept = Object.keys(item || {}).filter((key) => key.startsWith(`${prefix}:`) && item[key] != null);
    if (new Set([...kept, ...keys]).size > EXTRA_FIELDS_MAX) {
      throw new Error(`"${prefix}": at most ${EXTRA_FIELDS_MAX} fields of one interface on an item`);
    }
  }
}

/**
 * An item's place and identity are the engine's: `move` changes the place,
 * `add` gives the id (or takes a new one); a field that names them would
 * rewrite the tree around the item (two parents, a lost subtree).
 */
const STRUCTURE = ["id", "children", "childIds", "parentId", "order"];
function checkStructure(fields, { adding = false } = {}) {
  for (const key of STRUCTURE) {
    if (!(key in fields)) continue;
    if (adding && key === "id" && fields.id != null) {
      if (typeof fields.id !== "string" || !fields.id) throw new Error('"id": a new item\'s id is a non-empty string');
      if (MapState.find(fields.id)) throw new Error(`"${fields.id}": an item with this id is already in the map`);
      continue;
    }
    if (adding && key === "children" && Array.isArray(fields.children) && fields.children.length === 0) continue;
    throw new Error(`"${key}": not a field an interface sets (add items one by one; move places them)`);
  }
}

/** Refused, and said once in the console, when this person may not change the map. */
function writable() {
  if (!MapState.isReadOnly()) return true;
  console.warn("[Engine] Refused: this map is read-only for you");
  return false;
}

/**
 * A map's entry for an interface: a copy, without its keys' wrapped forms or
 * proofs, nor the secret of the link it was opened by (`viaLink` says so).
 */
const KEY_FIELDS = ["wrappedDEK", "previousWrappedDEKs", "keyAuthority", "_keyAuthorityConflict", "link"];
function entryForInterface(entry) {
  if (!entry) return null;
  const copy = structuredClone(entry);
  if (entry.link) copy.viaLink = true;
  for (const field of KEY_FIELDS) delete copy[field];
  return copy;
}

/**
 * Plain data given to the engine, as this window's own: an interface in a
 * frame (interfaces/index.js) has its own Object and Array, which the map's
 * format refuses ("Unexpected content type").
 */
const own = (value) => (value !== null && typeof value === "object" ? structuredClone(value) : value);

/** Whether an item's note is all Markdown can say (no note: true). */
const markdownOnly = (id) => Bridge.isNodeNoteMarkdownOnly(id);

/** Todo, in progress, done (from the status tags), or null. */
function status(itemOrId) {
  const item = typeof itemOrId === "string" ? MapState.find(itemOrId) : itemOrId;
  if (!item) return null;
  return Bridge.getEffectiveStatus(item) || null;
}

const map = {
  /** The open map's id, or null. */
  id: () => Bridge.getActiveDocId() || null,
  /** The tree (read-only; linked maps' content included), or null. */
  tree: () => MapState.get("data") || null,
  /** One item, or null. */
  item: (id) => MapState.find(id) || null,
  /** An item's parent, or null (the root has none). */
  parentOf: (id) => MapState.findParent(id) || null,
  /** A new item's id, for an interface that names an item before adding it. */
  newId: () => Bridge.generateNodeId(),
  /** An item's fields of one interface, without the prefix: { effort: "M" }. */
  extra(itemOrId, prefix) {
    const item = typeof itemOrId === "string" ? MapState.find(itemOrId) : itemOrId;
    const fields = {};
    for (const [key, value] of Object.entries(item || {})) {
      if (key.startsWith(`${prefix}:`) && value != null) fields[key.slice(prefix.length + 1)] = value;
    }
    return fields;
  },
  /** Whether the map, or this item, cannot be changed by this person. */
  isReadOnly: (id) => (id ? !!Bridge.isNodeReadOnly(id) : !!MapState.isReadOnly()),

  /**
   * Add an item under `parentId` (at `index`, -1: last). Returns its id, or
   * null when it was refused (read-only, a full map).
   * @param {string} parentId
   * @param {Object} [fields] - name, color, notes, tags, startDate, endDate…
   * @param {number} [index]
   */
  add(parentId, fields = {}, index = -1) {
    requireMap();
    fields = own(fields);
    checkStructure(fields, { adding: true });
    checkExtraFields(fields);
    const node = Bridge.addNode(parentId, { ...fields, id: fields.id || Bridge.generateNodeId(), children: [] }, index);
    return node?.id ?? null;
  },
  /**
   * Change an item's fields (`notes` as Markdown). Returns false when refused
   * (also when `notes` would replace a note Markdown cannot keep: see
   * notes.markdownOnly; nothing is written then).
   */
  update(id, fields) {
    requireMap();
    fields = own(fields);
    checkStructure(fields);
    checkExtraFields(fields, MapState.find(id));
    if (typeof fields.notes === "string" && !markdownOnly(id)) return false;
    return Bridge.updateNode(id, fields) !== false;
  },
  /**
   * Move items under `parentId` (at `index`, -1: last), in their order.
   * @param {string|string[]} ids
   */
  move(ids, parentId, index = -1) {
    requireMap();
    const list = Array.isArray(ids) ? ids : [ids];
    for (const id of list) {
      if (id === MapState.get("data")?.id) throw new Error("The map's root cannot be moved");
      // Under itself or one of its own items: a loop the tree cannot show.
      for (let at = parentId; at; at = MapState.findParent(at)?.id) {
        if (at === id) throw new Error(`"${id}" cannot go under itself or one of its own items`);
      }
    }
    return list.length === 1 ? Bridge.moveNode(list[0], parentId, index) : Bridge.moveNodes(list, parentId, index);
  },
  /** Remove items (with what they hold). The root stays: deleting a map is maps.delete. */
  remove(ids) {
    requireMap();
    const list = Array.isArray(ids) ? ids : [ids];
    if (list.includes(MapState.get("data")?.id)) throw new Error("The map's root cannot be removed (maps.delete deletes a map)");
    map.batch(() => { for (const id of list) Bridge.removeNode(id); });
  },
  /** Several changes as one: one save, one undo step. */
  batch(fn) {
    Bridge.beginBatch();
    try {
      return fn();
    } finally {
      Bridge.endBatch();
    }
  },
  /** Undo and redo this person's own changes in the open map. */
  undo: () => Undo.undo(),
  redo: () => Undo.redo(),
  canUndo: () => Undo.canUndo(),
  canRedo: () => Undo.canRedo(),

  tags: {
    /** The map's tag definitions. */
    definitions: () => Bridge.getTagDefinitions() || [],
    /**
     * Add a tag definition ({ name, color… }): an id and a colour not in use
     * are given when missing. Returns it.
     */
    define(tag) {
      requireMap();
      if (!writable()) return null;
      const used = (Bridge.getTagDefinitions() || []).map((definition) => definition.color).filter(Boolean);
      const { mandatory: _mandatory, ...given } = own(tag) || {};
      const definition = { id: Bridge.generateNodeId(), color: newItemColor(used), ...given };
      Bridge.addTagDefinition(definition);
      return definition;
    },
    /** Change a tag's name, colour… (not its id, nor whether it is one of the status tags). */
    change(tagId, changes) {
      if (!writable()) return false;
      const { id: _id, mandatory: _mandatory, ...allowed } = own(changes) || {};
      return Bridge.updateTagDefinition(tagId, allowed);
    },
    remove: (tagId) => writable() && Bridge.deleteTagDefinition(tagId),
    /** Put a tag on items, or take it off. */
    set: (ids, tagId, on = true) => Bridge.setNodesTag(Array.isArray(ids) ? ids : [ids], tagId, on),
    has: (item, tagId) => Bridge.nodeHasTag(item, tagId),
  },
  /** "todo", "in-progress", "done" or null, from the status tags. */
  status,

  notes: {
    /**
     * An item's note as Markdown ("" when none), as it is now. (The tree's
     * copy of a note being typed follows after a pause, in place.)
     */
    read: (id) => Bridge.readNodeNotes(id) ?? (MapState.find(id)?.notes || ""),
    /**
     * Whether a note is all Markdown can say (written back from its
     * Markdown, it is the same note). False for formatting Markdown loses
     * (underline, empty lines, table column widths…): an interface that
     * edits notes as Markdown shows such a note read-only.
     */
    markdownOnly,
    /**
     * Replace an item's note (Markdown). Refused (false) for a note Markdown
     * cannot keep, unless `{ replaceRich: true }` says the person chose to.
     */
    write: (id, markdown, { replaceRich = false } = {}) =>
      (replaceRich || markdownOnly(id)) && Bridge.updateNode(id, { notes: markdown }) !== false,
    /** The live collaborative note, for rich editors that bind to it, or null. */
    live: (id) => Bridge.getNodeNotesFragment(id) || null,
  },

  /** The map's journal: dated notes. */
  journal: {
    list: () => Bridge.getDocNotes() || [],
    add: (note) => writable() && Bridge.addDocNote(own(note)),
    update: (noteId, changes) => writable() && Bridge.updateDocNote(noteId, own(changes)),
    remove: (noteId) => writable() && Bridge.removeDocNote(noteId),
    /** A journal note as Markdown, as it is now. */
    read: (noteId) => (Bridge.isCollabDocNotesEnabled()
      ? Bridge.serializeDocNote(noteId) || ""
      : (Bridge.getDocNotes() || []).find((n) => n.id === noteId)?.content || ""),
    live: (noteId) => Bridge.getDocNoteFragment(noteId) || null,
  },

  routines: {
    state: () => Bridge.getRoutinesState(),
    change: (action, data) => writable() && Bridge.changeRoutine(action, own(data)),
  },

  /** The Today lists (execution) and postponed tasks. */
  today: {
    items: (options) => Bridge.getExecutionItems(options),
    add: (noteId, itemId, options) => Bridge.sendToExecution(noteId, itemId, own(options)),
    remove: (noteId, itemId) => Bridge.removeFromExecution(noteId, itemId),
    worked: (noteId, itemId, date, on = true) => Bridge.tickWorked(noteId, itemId, date, on),
    pin: (noteId, itemId, pinned) => Bridge.setExecutionPinned(noteId, itemId, pinned),
    order: (noteId, itemIds) => Bridge.setExecutionOrder(noteId, itemIds),
    canPostpone: (itemId) => Bridge.canPostponeTask(itemId),
    postpone: (itemId, until) => Bridge.postponeTask(itemId, until),
    bringBack: (itemId) => Bridge.bringTaskBack(itemId),
  },

  board: {
    columns: () => Bridge.getKanbanColumns(),
    setColumns: (columns) => writable() && Bridge.setKanbanColumns(own(columns)),
  },

  /** A linked map's state: "loaded", "no-access", "gone", "offline" or "not-open". */
  linkState(mapId) {
    // A map gone is also one nobody here can open: "gone" first (it was
    // never returned before 2026-10-26, the simulation found it).
    if (Bridge.isLinkedDocGone(mapId)) return "gone";
    if (Bridge.isLinkedDocAccessDenied(mapId)) return "no-access";
    if (Bridge.isLinkedDocOffline(mapId)) return "offline";
    return Bridge.hasLinkedDoc(mapId) ? "loaded" : "not-open";
  },

  /** Sync: connection, busy, fully synced, writes not yet saved. */
  sync: syncState,

  /** Where the open map is stored: "online", "local" (this device only) or null. */
  storage: () => openMapStorage(),

  /**
   * Files attached to the open map. A note refers to one as
   * "attachment:MAP/FILE" (a link, or an image).
   */
  attachments: {
    /**
     * Attach a file: resolves with { ref, name, type, waiting } (waiting:
     * kept on this device until it can be sent). Without onProgress the
     * interface shows the progress (engine/host.js runOperation).
     */
    async add(file, { onProgress } = {}) {
      const mapId = requireMap();
      const key = Bridge.getActiveEncryptionKey() || getAttachmentDek(mapId);
      if (!key) throw new Error("The map's key is not available");
      const { attachmentId, filename, mimeType, waiting } = await uploadAttachment(mapId, file, key, onProgress ? { onProgress } : {});
      // Shown at once from the file itself.
      populateAttachmentCache(mapId, attachmentId, { blob: file, mimeType, filename });
      return { ref: attachmentRef(mapId, attachmentId), name: filename, type: mimeType, waiting: !!waiting };
    },
    /** A URL to show or download an attachment (its reference), or null. */
    async url(ref) {
      const parsed = parseAttachmentRef(ref);
      return parsed ? resolveAttachmentForDisplay(parsed.docId, parsed.attachmentId) : null;
    },
    /** { mapId, attachmentId } from a reference, or null. */
    parse(ref) {
      const parsed = parseAttachmentRef(ref);
      return parsed ? { mapId: parsed.docId, attachmentId: parsed.attachmentId } : null;
    },
  },
};

// ---------------------------------------------------------------------------
// The maps (vault) and opening one
// ---------------------------------------------------------------------------

const maps = {
  /** The account's maps: { docId, title, folderId, role, _storage… } (copies, without their keys). */
  list: () => (VaultStorage.getIndex()?.docs || []).map(entryForInterface),
  /** The folders (copies). */
  folders: () => structuredClone(VaultStorage.getIndex()?.folders || []),
  /** One map's entry (a copy, without its keys), or null. */
  get: (mapId) => entryForInterface(VaultStorage.getDoc(mapId)),
  /** Create a map; returns { docId } (it opens with `open`). */
  create: (title, folderId = null) => VaultStorage.createMap(title, folderId),
  rename: (mapId, title) => VaultStorage.renameMap(mapId, title),
  moveToFolder: (mapId, folderId) => VaultStorage.moveMap(mapId, folderId),
  /**
   * Open a map (the open one is closed first). Resolves once it is open, with
   * { mapId, isLocal, isShared, shareRole, isNew, synced }, or null when it
   * did not open (a notice says why). Options: onProgress(fraction, stage),
   * onOpened(tree, info) (the screen state, before anything is drawn),
   * remember (default true): the next start on this device opens it, in
   * whichever interface.
   */
  async open(mapId, { remember = true, ...options } = {}) {
    const info = await openMap(mapId, options);
    if (info && remember) setLastOpenedDoc(mapId);
    return info;
  },
  /** Close the open map: its sync stops, no map is open. */
  async close() {
    await closeMap();
    MapState.reset();
  },
  /**
   * Delete a map (an online one from the server too, by its owner), with its
   * files waiting to be sent. The open one is closed: the interface opens
   * another.
   */
  async delete(mapId) {
    const wasOpen = MapState.get("currentDocId") === mapId;
    // Shared: its members removed first, each keeping a copy (the server
    // deletes no map that still has members).
    const entry = VaultStorage.getDoc(mapId);
    if (entry && storageOf(entry) === "online" && (!entry.role || entry.role === "owner")) {
      await removeAllMembers(mapId);
    }
    await VaultStorage.deleteDoc(mapId);
    await deleteWaitingFiles([mapId]).catch(() => {});
    if (wasOpen) await maps.close();
  },
  /**
   * A full copy of a map (notes, Today lists, files), next to it or in
   * `folderId`: { imported, errors, syncErrors, newDocIds }.
   */
  copy: async (mapId, { title, folderId } = {}) =>
    (await import("./services/map-copy.js")).copyMap(VaultStorage, mapId, { title, folderId }),
  /** Folders: a new one ({ parentId: null } is the top), rename, move. */
  createFolder: (name, parentId = null) => VaultStorage.createFolder(name, parentId),
  renameFolder: (folderId, name) => VaultStorage.renameFolder(folderId, name),
  moveFolder: (folderId, parentId) => VaultStorage.moveFolder(folderId, parentId),
  /** Delete a folder; one with maps or folders needs where they go. */
  deleteFolder: (folderId, moveContentsTo = null) => VaultStorage.deleteFolder(folderId, moveContentsTo),
  /**
   * Export maps (all without `mapIds`): { exportData, exportErrors }.
   * JSON.stringify(exportData) is a backup file `import` reads (files not
   * included; the classic interface also writes archives with them).
   */
  export: ({ mapIds, includeAttachments = false } = {}) => exportAllData({ docIds: mapIds, includeAttachments }),
  /**
   * Import a backup file (.json, or a .zip archive) next to the maps already
   * here: { imported, skipped, errors, syncErrors }.
   */
  async import(file) {
    const data = await readExportFile(file);
    if (!Array.isArray(data?.documents)) throw new Error(t("vault.import.chooseBackup"));
    return importAllData(data, { mergeWithExisting: true, keepCurrentDocument: true });
  },
};

/**
 * A map's history (docs/plans/2026-10-21-map-history.md): versions kept by
 * the server for an online map, read and compared here. A moment `at` is
 * `{ version, change, before }`: a version id (null: the current base),
 * its changes up to `change` (included; undefined: all; null: none), or up
 * to the one before it (`before`). Sittings and versions from `list()` give
 * the moments to use.
 */
const history = {
  /** The map's versions and sittings, with who made them. */
  list: (mapId) => History.list(mapId),
  /** The map at a moment, readable: { rootId, items (id → item, in the map's order), tags }. */
  async items(mapId, at) {
    const doc = await History.moment(mapId, at);
    try { return await History.itemsOf(doc); } finally { doc.destroy(); }
  },
  /**
   * What changed between two moments: { changes, before, after, people }
   * (items of each, and who changed the map in between). `sessions`: the
   * editing sessions the server recorded for that span (a version's), to
   * check the names against.
   */
  changes: (mapId, from, to, { sessions } = {}) => History.changesBetween(mapId, from, to, { sessions }),
  /** Bring items back into the open map as they were at a moment (editors). */
  async bringBack(mapId, at, ids) {
    if (!writable()) return null;
    const doc = await History.moment(mapId, at);
    try { return History.bringBack(mapId, doc, ids); } finally { doc.destroy(); }
  },
  /** The owner makes the open map as it was at a moment (one change, undoable). */
  async revert(mapId, at) {
    if (!writable()) return null;
    const doc = await History.moment(mapId, at);
    try { return await History.revert(mapId, doc); } finally { doc.destroy(); }
  },
  /** A new map holding the map as it was at a moment. */
  async copy(mapId, at, { title } = {}) {
    const doc = await History.moment(mapId, at);
    try { return await History.restoreAsNewMap(mapId, doc, { title }); } finally { doc.destroy(); }
  },
  /** Whether this account owns the map (only the owner reverts and manages history). */
  isOwner: (mapId) => History.isOwner(mapId),
  /** The owner: delete one version, the whole history, or set how many versions to keep. */
  deleteVersion: (mapId, versionId) => History.deleteVersion(mapId, versionId),
  deleteAll: (mapId) => History.deleteAll(mapId),
  setMaxVersions: (mapId, maxVersions) => History.setMaxVersions(mapId, maxVersions),
};

const account = {
  /** The signed-in user ({ id, username… }), or null. */
  user: () => getCurrentUser() || null,
  isSignedIn: () => !!getCurrentUser(),
  /**
   * Sign out ({ everywhere }): changes are saved first, and what exists only
   * on this device is checked (the person is asked through the host).
   */
  signOut: (options) => logout(options),
};

/**
 * Sharing (docs/architecture/sharing.md). `link`: a map's link
 * (docs/plans/2026-10-28-link-sharing.md, "Redesign": the link is the
 * access). The owner: get(mapId) (null when none), address(mapId) (owner and
 * admins: the link to copy), create(mapId, role), setRole(mapId, role),
 * turnOff(mapId, progress), renew(mapId, role, progress) (a new link, the
 * old one stops); turnOff and renew change the map key (`progress`:
 * { report, signal, setCancellable }). Whoever holds it: isLink(text),
 * open(text) (the map checked; keep() keeps it in the vault, marked
 * `viaLink`, keepForPage() for this page only), isLinkMap(mapId),
 * isGone(mapId) (the link no longer works), held(mapId) ({ role, gone,
 * kept, address }, or null), remove(mapId) (from this account's maps;
 * nothing on the server). Roles: 'viewer' or 'editor'.
 */
const sharing = {
  link: {
    get: (mapId) => LinkSharing.get(mapId),
    address: (mapId) => LinkSharing.address(mapId),
    create: (mapId, role) => LinkSharing.create(mapId, role),
    setRole: (mapId, role) => LinkSharing.setRole(mapId, role),
    turnOff: (mapId, progress) => LinkSharing.turnOff(mapId, progress),
    renew: (mapId, role, progress) => LinkSharing.renew(mapId, role, progress),
    isLink: (text) => LinkSharing.isLink(text),
    open: (text) => LinkSharing.open(text),
    isLinkMap: (mapId) => LinkSharing.isLinkMap(mapId),
    isGone: (mapId) => LinkSharing.isGone(mapId),
    held: (mapId) => LinkSharing.held(mapId),
    remove: (mapId) => LinkSharing.remove(mapId),
  },
};

/** Frozen through: an interface cannot replace a function classic also calls. */
function deepFreeze(object) {
  for (const value of Object.values(object)) {
    if (value && typeof value === "object" && !Object.isFrozen(value)) deepFreeze(value);
  }
  return Object.freeze(object);
}
deepFreeze(map);
deepFreeze(maps);
deepFreeze(history);
deepFreeze(account);
deepFreeze(sharing);

/** The engine (one per page). */
export const engine = Object.freeze({
  map, maps, history, account, sharing, on,
  /** The interface registers how the engine reaches the person (engine/host.js). */
  setHost,
});

/** The engine, as `createEngine()` will return it once there can be several. */
export function createEngine() {
  return engine;
}
